import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, DollarSign, Calculator } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { safeLog } from '@/utils/safeLogger';

interface PricingConfig {
  id: string;
  price_per_1k_tokens: number;
  baseline_extraction: number;
  baseline_smart_categories: number;
  baseline_smart_notes: number;
  credit_hardcap: number;
  updated_at: string;
  updated_by: string | null;
}

interface HistoryEntry {
  id: string;
  config_snapshot: any;
  changed_by: string | null;
  changed_at: string;
}

export function PricingSettings() {
  const [config, setConfig] = useState<PricingConfig | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [tokenPrice, setTokenPrice] = useState(0.01);
  const [extractionPrice, setExtractionPrice] = useState(1.0);
  const [categoriesPrice, setCategoriesPrice] = useState(0.5);
  const [notesPrice, setNotesPrice] = useState(2.0);
  const [hardcap, setHardcap] = useState(10);

  useEffect(() => {
    loadConfig();
    loadHistory();
  }, []);

  const loadConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('system_pricing_config')
        .select('*')
        .single();

      if (error) throw error;

      setConfig(data);
      setTokenPrice(Number(data.price_per_1k_tokens));
      setExtractionPrice(Number(data.baseline_extraction));
      setCategoriesPrice(Number(data.baseline_smart_categories));
      setNotesPrice(Number(data.baseline_smart_notes));
      setHardcap(Number(data.credit_hardcap));
    } catch (error) {
      safeLog.error('Error loading pricing config', error as Error);
      toast.error('Error loading pricing configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('system_pricing_config_history')
        .select('*')
        .order('changed_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      safeLog.error('Error loading pricing history', error as Error);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('system_pricing_config')
        .update({
          price_per_1k_tokens: tokenPrice,
          baseline_extraction: extractionPrice,
          baseline_smart_categories: categoriesPrice,
          baseline_smart_notes: notesPrice,
          credit_hardcap: hardcap,
          updated_at: new Date().toISOString(),
          updated_by: user?.id || null
        })
        .eq('id', config.id);

      if (error) throw error;

      toast.success('Pricing settings saved!');
      await loadConfig();
      await loadHistory();
    } catch (error) {
      safeLog.error('Error saving pricing config', error as Error);
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="glass-card-strong hover-lift transition-all">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500/10 to-emerald-500/10 flex items-center justify-center animate-float">
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-gradient">Pricing Configuration</CardTitle>
              <CardDescription>
                Dynamic pricing settings for credit usage. Changes take effect immediately for all new operations.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Token Pricing */}
          <div className="space-y-2">
            <Label htmlFor="tokenPrice">Price per 1,000 Tokens (Credits)</Label>
            <Input
              id="tokenPrice"
              type="number"
              step="0.0001"
              min="0"
              value={tokenPrice}
              onChange={(e) => setTokenPrice(parseFloat(e.target.value) || 0)}
            />
            <p className="text-xs text-muted-foreground">
              Current: {tokenPrice.toFixed(4)} Credits per 1k Tokens
            </p>
          </div>

          {/* Feature Baselines */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="extractionPrice">Extraction (Baseline)</Label>
              <Input
                id="extractionPrice"
                type="number"
                step="0.1"
                min="0"
                value={extractionPrice}
                onChange={(e) => setExtractionPrice(parseFloat(e.target.value) || 0)}
              />
              <p className="text-xs text-muted-foreground">Base Feature</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="categoriesPrice">Smart Categories (+)</Label>
              <Input
                id="categoriesPrice"
                type="number"
                step="0.1"
                min="0"
                value={categoriesPrice}
                onChange={(e) => setCategoriesPrice(parseFloat(e.target.value) || 0)}
              />
              <p className="text-xs text-muted-foreground">Additional</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notesPrice">Smart Notes (+)</Label>
              <Input
                id="notesPrice"
                type="number"
                step="0.1"
                min="0"
                value={notesPrice}
                onChange={(e) => setNotesPrice(parseFloat(e.target.value) || 0)}
              />
              <p className="text-xs text-muted-foreground">Additional</p>
            </div>
          </div>

          {/* Hardcap */}
          <div className="space-y-2">
            <Label htmlFor="hardcap">Credit Hardcap (max. per operation)</Label>
            <Input
              id="hardcap"
              type="number"
              min="1"
              value={hardcap}
              onChange={(e) => setHardcap(parseInt(e.target.value) || 1)}
            />
            <p className="text-xs text-muted-foreground">
              Maximum credits per operation, regardless of token usage
            </p>
          </div>

          {/* Preview Calculator */}
          <Card className="bg-muted">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Cost Calculator (Preview)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Extraction Only:</span>
                <span className="font-semibold">{extractionPrice.toFixed(2)} Credits</span>
              </div>
              <div className="flex justify-between">
                <span>Extraction + Smart Categories:</span>
                <span className="font-semibold">{(extractionPrice + categoriesPrice).toFixed(2)} Credits</span>
              </div>
              <div className="flex justify-between">
                <span>Extraction + Smart Notes:</span>
                <span className="font-semibold">{(extractionPrice + notesPrice).toFixed(2)} Credits</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">All Features:</span>
                <span className="font-bold">{(extractionPrice + categoriesPrice + notesPrice).toFixed(2)} Credits</span>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Token costs may apply additionally. Maximum: {hardcap} Credits per operation.
              </p>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Change History */}
      {history.length > 0 && (
        <Card className="glass-card-strong hover-lift transition-all">
          <CardHeader>
            <CardTitle className="text-gradient">Change History</CardTitle>
            <CardDescription>Last 10 Pricing Changes</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Token Price</TableHead>
                  <TableHead>Extraction</TableHead>
                  <TableHead>Categories</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Hardcap</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-xs">
                      {new Date(entry.changed_at).toLocaleString('de-DE')}
                    </TableCell>
                    <TableCell>{entry.config_snapshot.price_per_1k_tokens}</TableCell>
                    <TableCell>{entry.config_snapshot.baseline_extraction}</TableCell>
                    <TableCell>{entry.config_snapshot.baseline_smart_categories}</TableCell>
                    <TableCell>{entry.config_snapshot.baseline_smart_notes}</TableCell>
                    <TableCell>{entry.config_snapshot.credit_hardcap}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
